import { useRef } from 'react';
import { getFramework, getTrackFunc, IExportLayerOriginInfo, useIdentity } from '@/framework';
import { ITouchKitEvent } from '@/utils/event-kit';
import { getLayerCenterPoint } from '@/utils/layer-kit';
import { degreeBetweenPoints } from '@/utils/math-kit';
import { BaseLayerViewType, type ILayerProps, type IPoint } from '@/version-types';
import { MouseOperate } from '../../mouse-operate';
import { useStickyInfo } from './use-sticky-info';

export const useRotateOperate = () => {
    const identity = useIdentity();
    const oldInfo = useRef<ILayerProps>();
    const originLayerInfo = useRef<IExportLayerOriginInfo>();
    const centerPoint = useRef<IPoint>({ x: 0, y: 0 });
    const startPoint = useRef<IPoint>({ x: 0, y: 0 });
    const framework = getFramework(identity);
    const mouseOperate = framework.service.registration.getInstance<MouseOperate>('MouseOperate');
    const sticky = useStickyInfo();

    return {
        /** 准备工作, 用于存储一些值 */
        beforeRotate(layerId: string, e: ITouchKitEvent) {
            const layer = framework.service.flatLayer.getLayer(layerId, true);
            if (!layer) {
                mouseOperate.emitCancel();
                return;
            }
            oldInfo.current = layer;
            originLayerInfo.current = framework.service.frame.exportLayerOriginInfo(layerId);
            framework.service.frame.__emitBeforeBaseOperate(layer, BaseLayerViewType.ROTATE);
            const retinaScaling = framework.service.edgelessCanvas.retinaScaling;
            centerPoint.current = getLayerCenterPoint(framework.service.flatLayer.convertLayer(layer)!, retinaScaling);
            startPoint.current = framework.service.transform.getScenePoint(e.originEvent);
            startPoint.current.x /= retinaScaling;
            startPoint.current.y /= retinaScaling;
            sticky.before();
            mouseOperate.emitRotateStart({
                layerId,
                oldLayer: layer,
                layer: mouseOperate.resolveCurrentOperateLayer(),
                pointerX: e.x,
                pointerY: e.y,
                isStart: e.isStart,
                isEnd: e.isEnd,
            });
        },

        /**
         * 旋转
         *
         * @param layerId - 图层 ID
         * @param e - 触摸事件对象
         */
        rotate(layerId: string, e: ITouchKitEvent) {
            if (!oldInfo.current || !originLayerInfo.current) return;
            if (!framework.service.flatLayer.getLayer(layerId)) {
                mouseOperate.emitCancel();
                return;
            }

            framework.service.editor.setIsBaseOperate(true);
            const retinaScaling = framework.service.edgelessCanvas.retinaScaling;
            const currentPoint = framework.service.transform.getScenePoint(e.originEvent);
            currentPoint.x /= retinaScaling;
            currentPoint.y /= retinaScaling;
            const originAngle =
                (degreeBetweenPoints(startPoint.current, currentPoint, centerPoint.current) / Math.PI) * 180;
            const angle = Math.round(originAngle);
            mouseOperate.emitRotateBeforeUpdate({
                layerId,
                oldLayer: oldInfo.current,
                layer: mouseOperate.resolveCurrentOperateLayer(),
                originAngle,
                rawAngle: angle,
                pointerX: e.x,
                pointerY: e.y,
                isStart: e.isStart,
                isEnd: e.isEnd,
            });
            const { childOriginInfoMap, originInfo } = originLayerInfo.current;
            const rotate = framework.service.frame.updateLayerRotate(layerId, angle, originInfo!, childOriginInfoMap, {
                isStart: e.isStart,
                isEnd: e.isEnd,
                enableAdsorb: true,
            });
            mouseOperate.emitRotateAfterUpdate({
                layerId,
                oldLayer: oldInfo.current,
                layer: mouseOperate.resolveCurrentOperateLayer(),
                rotate,
                pointerX: e.x,
                pointerY: e.y,
                isStart: e.isStart,
                isEnd: e.isEnd,
            });

            sticky.pending(layerId);

            if (e.isEnd) {
                sticky.clear();
                mouseOperate.emitRotateEnd({
                    layerId,
                    oldLayer: oldInfo.current,
                    layer: mouseOperate.resolveCurrentOperateLayer(),
                    rotate,
                    pointerX: e.x,
                    pointerY: e.y,
                    isStart: e.isStart,
                    isEnd: e.isEnd,
                });
                framework.service.editor.setIsBaseOperate(false);

                if (sticky.hasSticky()) {
                    sticky.after(oldInfo.current);
                } else {
                    // framework.service.frame.addOrUpdateLayer(framework.service.flatLayer.getLayer(layerId, true));
                    framework.service.history.write(oldInfo.current, getTrackFunc(identity, oldInfo.current, 'rotate'));
                    framework.service.frame.setActiveLayer(layerId);
                }
                framework.service.agentLayout.arrangeLayerFrame(layerId);
            }

            return rotate;
        },
    };
};
